/** @jsxImportSource @emotion/react */
'use memo'

import type { ReactNode } from 'react'
import css from '@emotion/css/macro'
import type { ColDef } from 'ag-grid-community'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { PLAYER_DETAILS_TABLE_SECTIONS } from '@sportninja/common/features/profile/player/playerDetails/constants/sections'
import type { PlayerDetailsTableSectionKey } from '@sportninja/common/features/profile/player/playerDetails/types'
import { ScrollSpyLayout } from 'src/components/ScrollSpyLayout'
import ColumnChooser from 'src/components/ts-components/ColumnChooser'
import DataGrid from 'src/components/ts-components/DataGrid'
import { useColumnVisibility } from 'src/hooks/useColumnVisibility'
import { PLAYER_DETAILS_SECTION_ICONS } from '../constants/sectionItems'
import {
  applyColumnVisibility,
  toColumnOptions,
} from '../utils/columnVisibility'

interface DetailTableSectionProps<TRow extends { id: string }> {
  sectionKey: PlayerDetailsTableSectionKey
  rows: TRow[]
  columnDefs: ColDef<TRow>[]
  loading: boolean
  /** Shown right after the title (e.g. a help link). */
  titleHint?: ReactNode
  /** Extra controls on the title row, before the column chooser. */
  actions?: ReactNode
  /** Replaces the grid body when set. */
  children?: ReactNode
}

/**
 * A read-only table section, drawn with the shared DataGrid, with a column
 * chooser on its title row. The chosen columns last while the modal is open —
 * no storage key, so nothing is remembered once it closes.
 */
const DetailTableSection = <TRow extends { id: string }>({
  sectionKey,
  rows,
  columnDefs,
  loading,
  titleHint,
  actions,
  children,
}: DetailTableSectionProps<TRow>) => {
  const section = PLAYER_DETAILS_TABLE_SECTIONS[sectionKey]
  const Icon = PLAYER_DETAILS_SECTION_ICONS[sectionKey]
  const columns = useColumnVisibility({ columns: toColumnOptions(columnDefs) })

  return (
    <ScrollSpyLayout.Section
      id={sectionKey}
      title={capitalizeWords(section.label)}
      icon={<Icon />}
      titleHint={titleHint}
      titleAccessory={
        <div css={accessoryStyle}>
          {actions}
          <ColumnChooser
            label='Columns'
            title={`Show ${section.label} Columns`}
            columns={columns.columns}
            onToggle={columns.setColumnVisible}
            onReset={columns.resetColumns}
            data-testid={`player-details-columns-${sectionKey}`}
          />
        </div>
      }
    >
      {children ?? (
        <div css={gridWrapStyle}>
          <DataGrid<TRow>
            rowData={rows}
            columnDefs={applyColumnVisibility(columnDefs, columns.isHidden)}
            getRowId={({ data }) => data.id}
            loading={loading}
            // Grows with its rows: these lists are short, and a fixed-height box
            // would nest a second scroller inside the section scroll.
            domLayout='autoHeight'
            noRowsOverlayComponentParams={{ message: section.emptyMessage }}
            data-testid={`player-details-grid-${sectionKey}`}
          />
        </div>
      )}
    </ScrollSpyLayout.Section>
  )
}

export default DetailTableSection

// ---- styled ----
const accessoryStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

// `min-width: 0` lets the grid scroll its own columns inside the section
// instead of widening the content column.
const gridWrapStyle = css`
  width: 100%;
  min-width: 0;
`
